import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, resolveImage, formatError } from "@/lib/api";

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export default function HomeConfig() {
  const [cfg, setCfg] = useState(null);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [catUploads, setCatUploads] = useState({}); // id -> uploading bool

  useEffect(() => {
    (async () => {
      const [h, c] = await Promise.all([
        api.get("/home"),
        api.get("/categories", { params: { include_hidden: true } }),
      ]);
      setCfg(h.data);
      setCats(c.data);
    })();
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const url = await uploadImage(file);
      set("logo_url", url);
      toast.success("Logo caricato");
    } catch (e) {
      toast.error("Upload logo fallito", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setUploadingLogo(false);
    }
  };

  const addHero = async (files) => {
    setUploadingHero(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      set("hero_images", [...(cfg.hero_images || []), ...urls]);
    } catch (e) {
      toast.error("Upload fallito", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setUploadingHero(false);
    }
  };

  const removeHero = (i) => set("hero_images", cfg.hero_images.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = cfg;
      await api.put("/admin/home", payload);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error("Errore", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setSaving(false);
    }
  };

  const uploadCatImage = async (cat, file) => {
    setCatUploads((s) => ({ ...s, [cat.id]: true }));
    try {
      const url = await uploadImage(file);
      await api.put(`/admin/categories/${cat.id}`, { ...cat, image_url: url });
      toast.success(`Immagine aggiornata: ${cat.name}`);
      const { data } = await api.get("/categories", { params: { include_hidden: true } });
      setCats(data);
    } catch (e) {
      toast.error("Upload fallito", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setCatUploads((s) => ({ ...s, [cat.id]: false }));
    }
  };

  const clearCatImage = async (cat) => {
    await api.put(`/admin/categories/${cat.id}`, { ...cat, image_url: null });
    const { data } = await api.get("/categories", { params: { include_hidden: true } });
    setCats(data);
  };

  if (!cfg) return <AdminLayout title="Aspetto sito" back="/admin"><p className="text-slate-500">Caricamento...</p></AdminLayout>;

  const subs = cats.filter((c) => c.parent_id && !c.hidden);

  return (
    <AdminLayout title="Aspetto sito" back="/admin">
      <div className="space-y-6 max-w-3xl">
        {/* Logo */}
        <Section title="Logo">
          <div className="flex items-center gap-4">
            <div className="w-28 h-20 rounded-xl bg-slate-100 border border-border overflow-hidden flex items-center justify-center">
              {cfg.logo_url ? (
                <img src={resolveImage(cfg.logo_url)} alt="logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-xs text-slate-400">Nessun logo</span>
              )}
            </div>
            <label className="cursor-pointer inline-flex items-center gap-2 h-10 px-4 rounded-full bg-brand-blue text-white text-sm font-bold">
              <Upload className="w-4 h-4" />
              {uploadingLogo ? "Caricamento..." : "Carica logo"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])}
                disabled={uploadingLogo}
                data-testid="upload-logo"
              />
            </label>
            <Input
              data-testid="logo-url"
              value={cfg.logo_url || ""}
              onChange={(e) => set("logo_url", e.target.value)}
              placeholder="Oppure incolla URL"
              className="flex-1 rounded-xl"
            />
          </div>
        </Section>

        {/* Home texts */}
        <Section title="Testi Home">
          <Field label="Titolo banner">
            <Input data-testid="banner-title" value={cfg.banner_title || ""} onChange={(e) => set("banner_title", e.target.value)} className="rounded-xl" />
          </Field>
          <Field label="Sottotitolo">
            <Textarea data-testid="banner-subtitle" value={cfg.banner_subtitle || ""} onChange={(e) => set("banner_subtitle", e.target.value)} rows={2} className="rounded-xl" />
          </Field>
          <Field label="Messaggio promozionale (opzionale)">
            <Input data-testid="promo-message" value={cfg.promo_message || ""} onChange={(e) => set("promo_message", e.target.value)} placeholder="Es: Spedizione gratis oltre € 50" className="rounded-xl" />
          </Field>
          <Field label="Titolo Benvenuti">
            <Input data-testid="welcome-title" value={cfg.welcome_title || ""} onChange={(e) => set("welcome_title", e.target.value)} className="rounded-xl" />
          </Field>
          <Field label="Testo Benvenuti">
            <Textarea data-testid="welcome-text" value={cfg.welcome_text || ""} onChange={(e) => set("welcome_text", e.target.value)} rows={3} className="rounded-xl" />
          </Field>
        </Section>

        {/* Hero images */}
        <Section title="Immagini Home">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {(cfg.hero_images || []).map((im, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-border">
                <img src={resolveImage(im)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removeHero(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <label className="aspect-square rounded-xl border-2 border-dashed border-brand-blue/40 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50 transition">
              <Upload className="w-6 h-6 text-brand-blue" strokeWidth={2.5} />
              <span className="text-[10px] mt-1 text-brand-blue font-bold uppercase">Aggiungi</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addHero(Array.from(e.target.files))} disabled={uploadingHero} />
            </label>
          </div>
        </Section>

        {/* eBay & Vinted */}
        <Section title="Riquadri eBay / Vinted">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Switch data-testid="ebay-enabled" checked={!!cfg.ebay_enabled} onCheckedChange={(v) => set("ebay_enabled", v)} />
                <Label className="font-bold">Mostra eBay</Label>
              </div>
              <Input data-testid="ebay-url" value={cfg.ebay_url || ""} onChange={(e) => set("ebay_url", e.target.value)} placeholder="URL eBay" className="rounded-xl" />
              <Input data-testid="ebay-label" value={cfg.ebay_label || ""} onChange={(e) => set("ebay_label", e.target.value)} placeholder="Testo pulsante" className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Switch data-testid="vinted-enabled" checked={!!cfg.vinted_enabled} onCheckedChange={(v) => set("vinted_enabled", v)} />
                <Label className="font-bold">Mostra Vinted</Label>
              </div>
              <Input data-testid="vinted-url" value={cfg.vinted_url || ""} onChange={(e) => set("vinted_url", e.target.value)} placeholder="URL Vinted" className="rounded-xl" />
              <Input data-testid="vinted-label" value={cfg.vinted_label || ""} onChange={(e) => set("vinted_label", e.target.value)} placeholder="Testo pulsante" className="rounded-xl" />
            </div>
          </div>
        </Section>

        {/* Social & Contatti */}
        <Section title="Social & Contatti">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Instagram URL"><Input data-testid="ig-url" value={cfg.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} className="rounded-xl" /></Field>
            <Field label="TikTok URL"><Input data-testid="tt-url" value={cfg.tiktok_url || ""} onChange={(e) => set("tiktok_url", e.target.value)} className="rounded-xl" /></Field>
            <Field label="WhatsApp URL"><Input data-testid="wa-url" value={cfg.whatsapp_url || ""} onChange={(e) => set("whatsapp_url", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Email contatti"><Input data-testid="contact-email" value={cfg.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="rounded-xl" /></Field>
          </div>
          <Field label="Testo footer"><Input data-testid="footer-msg" value={cfg.footer_message || ""} onChange={(e) => set("footer_message", e.target.value)} className="rounded-xl" /></Field>
        </Section>

        {/* Newsletter */}
        <Section title="Newsletter">
          <div className="flex items-center gap-2">
            <Switch data-testid="newsletter-enabled" checked={!!cfg.newsletter_enabled} onCheckedChange={(v) => set("newsletter_enabled", v)} />
            <Label className="font-bold">Mostra riquadro newsletter</Label>
          </div>
          <Field label="Testo newsletter"><Input data-testid="newsletter-text" value={cfg.newsletter_text || ""} onChange={(e) => set("newsletter_text", e.target.value)} className="rounded-xl" /></Field>
        </Section>

        {/* Sticky save */}
        <div className="sticky bottom-0 bg-background pt-3 pb-4">
          <Button data-testid="save-home-btn" onClick={save} disabled={saving} className="rounded-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-heading font-bold w-full sm:w-auto">
            {saving ? "Salvataggio..." : "Salva tutte le modifiche"}
          </Button>
        </div>
      </div>

      {/* Category images (below main form) */}
      <div className="max-w-3xl mt-8">
        <h2 className="font-heading font-black text-xl text-brand-blue mb-2">Immagini/loghi categorie</h2>
        <p className="text-sm text-slate-500 mb-3">Carica un logo/immagine per ogni sottocategoria — sarà mostrata nella Home e nel catalogo.</p>
        <div className="space-y-2">
          {subs.map((c) => (
            <div key={c.id} data-testid={`cat-img-row-${c.slug}`} className="bg-white border border-border rounded-2xl p-3 flex gap-3 items-center">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 border border-border">
                {c.image_url ? <img src={resolveImage(c.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400 text-center">Nessuna</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{c.name}</div>
                <div className="text-xs text-slate-400 truncate">/{c.slug}</div>
              </div>
              <label className="cursor-pointer inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-brand-blue text-white text-xs font-bold">
                <Upload className="w-3.5 h-3.5" /> {catUploads[c.id] ? "..." : "Carica"}
                <input
                  type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files[0] && uploadCatImage(c, e.target.files[0])}
                  disabled={!!catUploads[c.id]}
                  data-testid={`upload-cat-${c.slug}`}
                />
              </label>
              {c.image_url && (
                <button onClick={() => clearCatImage(c)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" data-testid={`clear-cat-${c.slug}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-4 sm:p-5 space-y-3">
      <h2 className="font-heading font-bold text-brand-blue">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
